import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Confetti from "../magicui/confetti";

interface Milestone {
  days: number;
  icon: string;
  label: string;
  color: string;
}

interface StreakCelebrationProps {
  open: boolean;
  onClose: () => void;
  milestone: Milestone;
  habitName: string;
}

const StreakCelebration: React.FC<StreakCelebrationProps> = ({
  open,
  onClose,
  milestone,
  habitName,
}) => {
  const { t } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(true);

  const handleClose = () => {
    setShowConfetti(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <>
      {open && (
        <Confetti active={showConfetti} particleCount={100} duration={3000} />
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 6, px: 4 }}>
          <Box
            sx={{
              fontSize: "6rem",
              mb: 2,
              animation: "bounce 1s ease-in-out infinite",
              "@keyframes bounce": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(-20px)" },
              },
            }}
          >
            {milestone.icon}
          </Box>

          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: "bold",
              textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            {t("streaks.celebration.title")}
          </Typography>

          <Typography variant="h5" sx={{ mb: 2, opacity: 0.95 }}>
            {milestone.label}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              {milestone.days}
            </Typography>
            <Typography variant="h6">
              {t("streaks.days", { count: milestone.days })}
            </Typography>
          </Stack>

          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
            {t("streaks.celebration.message", {
              habitName,
              days: milestone.days,
            })}
          </Typography>

          <Button
            variant="contained"
            onClick={handleClose}
            size="large"
            sx={{
              bgcolor: "white",
              color: milestone.color,
              fontWeight: "bold",
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.9)",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s",
            }}
          >
            {t("streaks.celebration.continue")}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StreakCelebration;
